import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';

/** apps/web が集約する。ルート単位で遅延ロード（03 第 I 部 §2） */
type PageModule = { default: ComponentType };

function page(load: () => Promise<PageModule>): RouteObject['lazy'] {
  return async () => {
    const m = await load();
    return { Component: m.default };
  };
}

export const salesRoutes: RouteObject[] = [
  { path: '/sales', lazy: page(() => import('./pages/PipelinePage.js')) },
  { path: '/sales/deals/:id', lazy: page(() => import('./pages/DealDetailPage.js')) },
  { path: '/sales/quotes/:id', lazy: page(() => import('./pages/QuotePage.js')) },
  // SFA §15.3.4 品目マスター（見積の明細の「品目から選ぶ」）
  { path: '/sales/items', lazy: page(() => import('./pages/SalesItemsPage.js')) },
  { path: '/sales/reports', lazy: page(() => import('./pages/SalesReportPage.js')) },
  // gap-sales（§15.3.7「費用の入力」）: 流入元別の費用（月 × 流入元。読むのは標準レポートを見られる人・入力は sales.cost.manage。画面の中で権限を見る）
  { path: '/sales/reports/costs', lazy: page(() => import('./pages/reports/SourceCostsPage.js')) },
  // SFA §15.3.7 目標と実績（月・四半期・年度の目標の編集と履歴。画面の中で権限を確かめる）
  { path: '/sales/targets', lazy: page(() => import('./pages/SalesTargetsPage.js')) },
  // SFA 1A（SFA機能拡張設計書 §4）。見込み客・フォーム・見込み客の設定（/sales/leads… /sales/settings/forms… /sales/settings/leads）は crm のルート
  { path: '/sales/home', lazy: page(() => import('./pages/SalesHomePage.js')) },
  { path: '/sales/settings', lazy: page(() => import('./pages/SalesSettingsPage.js')) },
  // SFA §15.3.2 担当者の交代（引き継ぎ）。?userId= で移管元を開ける。権限（sales.handover.execute）は画面の中で見る
  { path: '/sales/handovers', lazy: page(() => import('./pages/HandoverPage.js')) },
  // gap-sales（§15.3.1）: 更新商談の候補の一覧（絞り込み・並び替え・候補から商談を作る / 見送る。商談の閲覧権限は画面の中で見る）
  { path: '/sales/renewals', lazy: page(() => import('./pages/RenewalCandidatesPage.js')) },
  // SFA §8.2 自由なレポート（一覧・作成 / 編集・実行。権限は画面の中で見る。/new は :id より先に当たる）
  { path: '/sales/reports/custom', lazy: page(() => import('./pages/reports/CustomReportListPage.js')) },
  { path: '/sales/reports/custom/new', lazy: page(() => import('./pages/reports/CustomReportEditPage.js')) },
  { path: '/sales/reports/custom/:id/edit', lazy: page(() => import('./pages/reports/CustomReportEditPage.js')) },
  { path: '/sales/reports/custom/:id', lazy: page(() => import('./pages/reports/CustomReportRunPage.js')) },
  // SFA 段階 5 §9.1 商談ステージ・画面項目（一覧・作成 / 編集・カスタム項目・項目閲覧権限。権限は画面の中で見る。固定のパスは :id より先）
  { path: '/sales/settings/pipelines', lazy: page(() => import('./pages/pipelines/PipelineListPage.js')) },
  { path: '/sales/settings/pipelines/new', lazy: page(() => import('./pages/pipelines/PipelineEditPage.js')) },
  { path: '/sales/settings/pipelines/fields', lazy: page(() => import('./pages/pipelines/DealCustomFieldsPage.js')) },
  { path: '/sales/settings/pipelines/policies', lazy: page(() => import('./pages/pipelines/DealFieldPoliciesPage.js')) },
  { path: '/sales/settings/pipelines/:id', lazy: page(() => import('./pages/pipelines/PipelineEditPage.js')) },
  // SFA 段階 5 §9.2 営業の自動処理（一覧・作成 / 編集（試算と公開・実行記録）・実行記録。権限 sales.automation.manage は画面の中で見る。/new と /executions は :id より先）
  { path: '/sales/automation', lazy: page(() => import('./pages/automation/AutomationRuleListPage.js')) },
  { path: '/sales/automation/new', lazy: page(() => import('./pages/automation/AutomationRuleEditPage.js')) },
  { path: '/sales/automation/executions', lazy: page(() => import('./pages/automation/AutomationExecutionsPage.js')) },
  { path: '/sales/automation/:id', lazy: page(() => import('./pages/automation/AutomationRuleEditPage.js')) },
  // SFA 段階 5 §9.5 AI の営業支援: 対応候補の一覧（商談を読める人）と受注予測の評価（sales.ai.manage。画面の中で権限を見る）
  { path: '/sales/suggestions', lazy: page(() => import('./pages/ai/AiSuggestionsPage.js')) },
  { path: '/sales/ai/win-model', lazy: page(() => import('./pages/ai/WinModelEvaluationPage.js')) },
];
